// Canvas-слой для отображения маркеров истории транспорта
// Оптимизация: рендерит маркеры на canvas, минуя React Virtual DOM
// Поддерживает поворот маркеров (course), клики и tooltips
import 'app/libs/leafletCanvasMarkers';
import { TransportMarkerIcon, TransportMarkerSquareIcon } from 'app/icons';
import Leaflet from 'leaflet';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { MapLayer, withLeaflet } from 'react-leaflet';
const TOOLTIP_SHOW_DELAY = 600; // Задержка показа tooltip в мс
/**
 * Создаём Leaflet Layer для canvas-маркеров
 */
const CanvasHistoryMarkersLayer = Leaflet.Layer.extend({
    initialize: function (options) {
        Leaflet.setOptions(this, options);
        this._markers = [];
        this._markerHits = [];
        this._iconCache = new Map();
        this._tooltipTimeout = null;
        this._currentTooltipMarker = null;
    },
    onAdd: function (map) {
        this._map = map;
        // Получаем pane из options или используем дефолтный
        const paneName = this.options.pane || 'overlayPane';
        const pane = map.getPane(paneName) || map.createPane(paneName);
        // Создаём canvas
        this._canvas = Leaflet.DomUtil.create('canvas', 'leaflet-history-markers-layer leaflet-layer');
        const originProp = Leaflet.DomUtil.testProp(['transformOrigin', 'WebkitTransformOrigin', 'msTransformOrigin']);
        this._canvas.style[originProp] = '50% 50%';
        this._canvas.style.pointerEvents = 'auto';
        // Явно устанавливаем zIndex для canvas (синхронно с pane)
        this._canvas.style.zIndex = '660';
        const size = map.getSize();
        this._canvas.width = size.x;
        this._canvas.height = size.y;
        this._context = this._canvas.getContext('2d');
        // Добавляем canvas в pane
        pane.appendChild(this._canvas);
        // Создаём tooltip элемент
        this._tooltip = Leaflet.DomUtil.create('div', 'leaflet-history-tooltip');
        this._tooltip.style.cssText = `
      position: absolute;
      background: rgba(40, 40, 40, 0.95);
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-family: Roboto, sans-serif;
      pointer-events: none;
      z-index: 1000;
      white-space: nowrap;
      opacity: 0;
      transition: opacity 0.15s ease;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    `;
        map.getContainer().appendChild(this._tooltip);
        // События карты
        map.on('moveend', this._reset, this);
        map.on('zoomend', this._reset, this);
        map.on('resize', this._reset, this);
        // События мыши
        Leaflet.DomEvent.on(this._canvas, 'click', this._handleClick, this);
        Leaflet.DomEvent.on(this._canvas, 'mousemove', this._handleMouseMove, this);
        Leaflet.DomEvent.on(this._canvas, 'mouseleave', this._handleMouseLeave, this);
        this._reset();
    },
    onRemove: function () {
        const map = this._map;
        map.off('moveend', this._reset, this);
        map.off('zoomend', this._reset, this);
        map.off('resize', this._reset, this);
        Leaflet.DomEvent.off(this._canvas, 'click', this._handleClick, this);
        Leaflet.DomEvent.off(this._canvas, 'mousemove', this._handleMouseMove, this);
        Leaflet.DomEvent.off(this._canvas, 'mouseleave', this._handleMouseLeave, this);
        if (this._canvas.parentNode) {
            this._canvas.parentNode.removeChild(this._canvas);
        }
        if (this._tooltip && this._tooltip.parentNode) {
            this._tooltip.parentNode.removeChild(this._tooltip);
        }
        this._clearTooltipTimeout();
        this._iconCache.clear();
    },
    setMarkers: function (markers) {
        this._markers = markers || [];
        this._renderMarkers();
    },
    setOnClick: function (callback) {
        this._onClick = callback;
    },
    _reset: function () {
        if (!this._map)
            return;
        const size = this._map.getSize();
        const topLeft = this._map.containerPointToLayerPoint([0, 0]);
        Leaflet.DomUtil.setPosition(this._canvas, topLeft);
        this._canvas.width = size.x;
        this._canvas.height = size.y;
        this._renderMarkers();
    },
    _renderMarkers: function () {
        if (!this._context || !this._markers)
            return;
        const map = this._map;
        const iconSize = this.options.iconSize || 30;
        // Очищаем canvas
        this._context.clearRect(0, 0, this._canvas.width, this._canvas.height);
        // Очищаем массив для hit detection
        this._markerHits = [];
        // Рендерим каждый маркер
        for (const marker of this._markers) {
            if (!marker || marker.lat === undefined || marker.lon === undefined)
                continue;
            const point = map.latLngToContainerPoint([marker.lat, marker.lon]);
            const halfSize = iconSize / 2;
            // Сохраняем информацию для hit detection
            this._markerHits.push({
                id: marker.id,
                x: point.x,
                y: point.y,
                radius: halfSize + 5,
                data: marker,
            });
            // Получаем иконку из кэша
            const isStopped = marker.speed === 0 || marker.can_speed === 0;
            const iconImg = this._getIcon(marker.color, isStopped);
            if (iconImg && iconImg.complete) {
                // Рисуем с поворотом если есть course
                if (marker.course && !isStopped) {
                    this._drawRotatedImage(iconImg, point.x, point.y, iconSize, marker.course);
                }
                else {
                    this._context.drawImage(iconImg, point.x - halfSize, point.y - halfSize, iconSize, iconSize);
                }
            }
        }
    },
    _getIcon: function (color, isStopped) {
        const cacheKey = `${color}_${isStopped}`;
        if (this._iconCache.has(cacheKey)) {
            return this._iconCache.get(cacheKey);
        }
        // Создаём SVG-иконку
        const iconHtml = isStopped
            ? ReactDOMServer.renderToString(React.createElement(TransportMarkerSquareIcon, { fill: color, htmlColor: color, strokeWidth: 2 }))
            : ReactDOMServer.renderToString(React.createElement(TransportMarkerIcon, { fill: color, htmlColor: color, strokeWidth: 2 }));
        const img = new Image();
        img.src = 'data:image/svg+xml;base64,' + btoa(iconHtml);
        // Кэшируем
        this._iconCache.set(cacheKey, img);
        return img;
    },
    _drawRotatedImage: function (img, x, y, size, angle) {
        const halfSize = size / 2;
        this._context.save();
        this._context.translate(x, y);
        this._context.rotate((angle * Math.PI) / 180);
        this._context.drawImage(img, -halfSize, -halfSize, size, size);
        this._context.restore();
    },
    _handleClick: function (event) {
        const rect = this._canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        // Проверяем попадание по маркеру
        let hitMarker = null;
        for (const hit of this._markerHits) {
            const distance = Math.sqrt(Math.pow((x - hit.x), 2) + Math.pow((y - hit.y), 2));
            if (distance <= hit.radius) {
                hitMarker = hit;
                break;
            }
        }
        if (hitMarker && this._onClick) {
            this._onClick(event, hitMarker.data);
            return;
        }
        // Если кликнули не по маркеру — эмулируем клик на полилинии
        const latlng = this._map.containerPointToLatLng(new Leaflet.Point(x, y));
        // Находим верхний слой (полилинию) под точкой и вызываем его click
        let bestLayer = null;
        let bestDist = 200; // порог
        this._map.eachLayer((layer) => {
            if (!(layer instanceof Leaflet.Polyline))
                return;
            const latlngs = layer.getLatLngs();
            for (let i = 0; i < latlngs.length - 1; i++) {
                const dist = this._distanceToSegment(latlng, latlngs[i], latlngs[i + 1]);
                if (dist < bestDist) {
                    bestDist = dist;
                    bestLayer = layer;
                }
            }
        });
        if (bestLayer) {
            bestLayer.fire('click', { latlng, originalEvent: event });
        }
    },
    // Расстояние от точки p до отрезка [a, b] (в метрах)
    _distanceToSegment: function (p, a, b) {
        const v = { x: b.lat - a.lat, y: b.lng - a.lng };
        const w = { x: p.lat - a.lat, y: p.lng - a.lng };
        const c1 = w.x * v.x + w.y * v.y;
        if (c1 <= 0)
            return this._distance(p, a);
        const c2 = v.x * v.x + v.y * v.y;
        if (c2 <= c1)
            return this._distance(p, b);
        const b2 = c1 / c2;
        const pb = { x: a.lat + v.x * b2, y: a.lng + v.y * b2 };
        return this._distance(p, pb);
    },
    _distance: function (p1, p2) {
        const R = 6371000; // метры
        const φ1 = (p1.lat * Math.PI) / 180;
        const φ2 = (p2.lat * Math.PI) / 180;
        const Δφ = ((p2.lat - p1.lat) * Math.PI) / 180;
        const Δλ = ((p2.lng - p1.lng) * Math.PI) / 180;
        const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    },
    _handleMouseMove: function (event) {
        var _a;
        const rect = this._canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        // Проверяем попадание для изменения курсора и tooltip
        let hoveredMarker = null;
        for (const hit of this._markerHits) {
            const distance = Math.sqrt(Math.pow((x - hit.x), 2) + Math.pow((y - hit.y), 2));
            if (distance <= hit.radius) {
                hoveredMarker = hit;
                break;
            }
        }
        this._canvas.style.cursor = hoveredMarker ? 'pointer' : 'auto';
        // Управление tooltip
        if (hoveredMarker) {
            // Если навели на новый маркер
            if (((_a = this._currentTooltipMarker) === null || _a === void 0 ? void 0 : _a.id) !== hoveredMarker.id) {
                this._hideTooltip();
                this._clearTooltipTimeout();
                // Запускаем таймер показа tooltip
                this._tooltipTimeout = setTimeout(() => {
                    this._showTooltip(hoveredMarker, event.clientX, event.clientY);
                }, TOOLTIP_SHOW_DELAY);
                this._currentTooltipMarker = hoveredMarker;
            }
            else {
                // Обновляем позицию tooltip при движении мыши
                this._updateTooltipPosition(event.clientX, event.clientY);
            }
        }
        else {
            // Ушли с маркера - скрываем tooltip
            this._hideTooltip();
            this._clearTooltipTimeout();
            this._currentTooltipMarker = null;
        }
    },
    _handleMouseLeave: function () {
        this._hideTooltip();
        this._clearTooltipTimeout();
        this._currentTooltipMarker = null;
        this._canvas.style.cursor = 'auto';
    },
    _clearTooltipTimeout: function () {
        if (this._tooltipTimeout) {
            clearTimeout(this._tooltipTimeout);
            this._tooltipTimeout = null;
        }
    },
    _showTooltip: function (markerHit, mouseX, mouseY) {
        if (!this._tooltip || !markerHit.data.name)
            return;
        this._tooltip.textContent = markerHit.data.name;
        this._tooltip.style.opacity = '1';
        this._updateTooltipPosition(mouseX, mouseY);
    },
    _hideTooltip: function () {
        if (this._tooltip) {
            this._tooltip.style.opacity = '0';
        }
    },
    _updateTooltipPosition: function (mouseX, mouseY) {
        if (!this._tooltip)
            return;
        const container = this._map.getContainer();
        const containerRect = container.getBoundingClientRect();
        // Позиционируем tooltip над курсором с небольшим смещением
        const offsetX = 15;
        const offsetY = -15;
        let left = mouseX - containerRect.left + offsetX;
        let top = mouseY - containerRect.top + offsetY;
        // Проверяем выход за границы контейнера
        const tooltipRect = this._tooltip.getBoundingClientRect();
        const rightEdge = left + tooltipRect.width;
        const bottomEdge = top + tooltipRect.height;
        if (rightEdge > containerRect.width) {
            left = mouseX - containerRect.left - tooltipRect.width - offsetX;
        }
        if (bottomEdge > containerRect.height) {
            top = mouseY - containerRect.top - tooltipRect.height - offsetY;
        }
        this._tooltip.style.left = `${left}px`;
        this._tooltip.style.top = `${top}px`;
    },
});
class MapLeafletHistoryMarkersLayer extends MapLayer {
    createLeafletElement(props) {
        const layer = new CanvasHistoryMarkersLayer({
            iconSize: props.iconSize,
            pane: props.pane,
        });
        layer.setMarkers(props.markers);
        layer.setOnClick(props.onMarkerClick);
        return layer;
    }
    updateLeafletElement(fromProps, toProps) {
        // Проверяем, изменились ли маркеры
        if (this._markersChanged(fromProps.markers, toProps.markers)) {
            this.leafletElement.setMarkers(toProps.markers);
        }
        // Обновляем callback
        if (fromProps.onMarkerClick !== toProps.onMarkerClick) {
            this.leafletElement.setOnClick(toProps.onMarkerClick);
        }
    }
    _markersChanged(from, to) {
        if (!from || !to)
            return true;
        if (from.length !== to.length)
            return true;
        // Быстрая проверка по ID и позиции
        for (let i = 0; i < from.length; i++) {
            if (from[i].id !== to[i].id)
                return true;
            if (from[i].lat !== to[i].lat || from[i].lon !== to[i].lon)
                return true;
            if (from[i].course !== to[i].course)
                return true;
        }
        return false;
    }
}
MapLeafletHistoryMarkersLayer.propTypes = {
    markers: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        lat: PropTypes.number.isRequired,
        lon: PropTypes.number.isRequired,
        course: PropTypes.number,
        speed: PropTypes.number,
        color: PropTypes.string,
        name: PropTypes.string,
    })),
    onMarkerClick: PropTypes.func,
    iconSize: PropTypes.number,
    pane: PropTypes.string,
};
MapLeafletHistoryMarkersLayer.defaultProps = {
    markers: [],
    onMarkerClick: () => { },
    iconSize: 30,
    pane: 'overlayPane',
};
export default withLeaflet(MapLeafletHistoryMarkersLayer);
